// @ts-strict-ignore
import React, { useContext, useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import { useAtomValue, useSetAtom } from 'jotai/react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useFeatureFlags } from '@node-space/hooks'
import {
  Box,
  Button,
  Box as Container,
  Icon,
  Pagination,
  Sheet,
  SidePanel,
  Tabs,
  useSidePanel,
  type Tab,
} from '@node-space/ui-components'
import { appBannersId } from 'components/app/AppBanners'
import { RequestErrorStatusCard } from 'components/Errors/RequestErrorStatusCard'
import { PageHeader } from 'components/header/PageHeader'
import { SidePanelHeaderTemplateTwo } from 'components/SidePanel/SidePanelHeaderTemplates/SidePanelHeaderTemplateTwo'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useBulkCapabiltiesQuery } from 'hooks/queries/Capabilities/useCapabiltiesQuery'
import { useCustomerQuery } from 'hooks/queries/Customers/useCustomerQuery'
import { useComponentHeight } from 'hooks/useComponentHeight'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { currentCustomerAtom, customerModelAtom } from 'pages/Customers/atoms/customersAtoms'
import { CustomerLayout } from 'pages/Customers/CustomerView/CustomerLayout'
import { CustomerPaymentLimitsTable } from 'pages/Customers/CustomerView/CustomerPaymentLimitsTable'
import { CustomerWallets } from 'pages/Customers/CustomerView/CustomerWallets'
import {
  CustomerDetailTabs,
  CustomerModels,
  type CustomerDetailTab,
} from 'pages/Customers/helpers/types'
import { useCustomerWalletFilters } from 'pages/Customers/hooks/useCustomerWalletFilters'
import { AddWallet } from 'pages/NestedCustomers/components/AddWallet'
import CustomerInfo from 'pages/NestedCustomers/components/CustomerInfo'
import CustomerWalletSidepanelDetails from 'pages/NestedCustomers/components/CustomerWallet/CustomerWalletSidepanelDetails'
import { useCustomerWalletDepositDetails } from 'pages/NestedCustomers/hooks/useCustomerWalletDepositDetails'
import { useCustomerWalletsTableRowData } from 'pages/NestedCustomers/hooks/useCustomerWalletsTableRowData'
import { WalletTransactionExportModal } from 'pages/WalletDetails/components/RecentActivity/WalletTransactionsExport/WalletTransactionExportModal'
import {
  fiatSendFormAtom,
  selectedFiatBeneficiaryAtom,
  selectedWalletAtom,
} from 'pages/Wallets/components/Pay/atoms/atoms'
import { WalletSheet } from 'pages/Wallets/components/Sheet/WalletSheet'
import { WalletActionsDialog } from 'pages/Wallets/components/WalletActionsDialog'
import { WalletHasFundsDialog } from 'pages/Wallets/components/WalletHasFundsDialog'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import { BulkCapabilityRequest } from 'types/capabilities/capabilities'
import {
  CustomerStatus,
  CustomerWallet,
  CustomerWalletDepositDetails,
  NestedCustomerDetails,
} from 'types/customers'
import { ActionIds, WalletTypes } from 'types/types'
import { WalletTransferType } from 'types/unifiedPayouts/unifiedPayouts'
import {
  WalletDialogTypes,
  WalletLifecycleActionTypes,
  type WalletDialogType,
  type WalletLifecycleAction,
} from 'types/wallets'
import { getWalletActionCapabilityRequestFilters } from 'utils/capabilities'
import { highlightRowBasedOnId } from 'utils/table/table'
import { roundNumberWithCommas } from 'utils/utils'
import { CustomerInfoSkeletonLoader } from './CustomerView/CustomerInfoSkeletonLoader'

const DIALOG_TYPE_TO_ACTION_MAP: Partial<Record<WalletDialogType, WalletLifecycleAction>> = {
  [WalletDialogTypes.BLOCK]: WalletLifecycleActionTypes.BLOCK,
  [WalletDialogTypes.UNBLOCK]: WalletLifecycleActionTypes.UNBLOCK,
  [WalletDialogTypes.TERMINATE]: WalletLifecycleActionTypes.TERMINATE,
}

export const Customer = () => {
  const { t } = useTranslation()
  const {
    enableFiatPayinCapabilityV2,
    enableFiatPayoutCapabilityV2,
    enablePaymentLimitsManagement,
  } = useFeatureFlags()
  const bannerSpacing = useComponentHeight(appBannersId)

  const { id: customerReference }: { id?: string } = useParams()
  const { canTrade, canEdit } = useMappedRoles()?.wallets || {}
  const { viewOnly: isViewOnlyRole } = useProfileContext() || {}
  const [, setWalletAction] = useContext(WalletActionContext)
  const { accountCapabilities } = useAccountsContext()
  const { isEpcEmbeddedCustomerWalletCreationCapabilityEnabled } = accountCapabilities || {}

  const setBeneficiaryAtom = useSetAtom(selectedFiatBeneficiaryAtom)
  const setFiatFormAtom = useSetAtom(fiatSendFormAtom)
  const setFiatBeneficiaryAtom = useSetAtom(selectedFiatBeneficiaryAtom)
  const setSourceWalletAtom = useSetAtom(selectedWalletAtom)
  const customerModel = useAtomValue(customerModelAtom)
  const setCurrentCustomerAtom = useSetAtom(currentCustomerAtom)

  const [activeTab, setActiveTab] = useState<CustomerDetailTab>(CustomerDetailTabs.OVERVIEW)
  const [isAddWalletSheetOpen, setIsAddWalletSheetOpen] = useState<boolean>(false)
  const [isSheetOpen, setIsSheetOpen] = useState<boolean>(false)
  const [currentCustomerWallet, setCurrentCustomerWallet] = useState<CustomerWallet>()
  const [isTransactionExportModalVisible, setIsTransactionExportModalVisible] = useState(false)
  const [walletDialogState, setWalletDialogState] = useState<{
    wallet: CustomerWallet
    dialogType: WalletDialogType
  } | null>(null)

  const {
    filterProps,
    customerWallets,
    isFetchingCustomerWallets,
    isErrorFetchingCustomerWallets,
    fetchCustomerWallets,
    pagination,
  } = useCustomerWalletFilters(customerReference)

  const hasAnyWallets =
    (filterProps?.fiatWalletCount ?? 0) > 0 || (filterProps?.cryptoWalletCount ?? 0) > 0

  const isEmbeddedOrRelianceCustomerModel =
    customerModel === CustomerModels.EMBEDDED || customerModel === CustomerModels.RELIANCE

  const {
    data: customer,
    isFetching: isFetchingCustomerInfo,
    isError: isErrorFetchingCustomerInfo,
    refetch: refetchCustomerInfo,
  } = useCustomerQuery<NestedCustomerDetails>(customerReference)

  const isFetchingWalletsData = useMemo(
    () => isFetchingCustomerWallets || isFetchingCustomerInfo,
    [isFetchingCustomerWallets, isFetchingCustomerInfo]
  )

  const fiatWalletCapabilitiesRequest: BulkCapabilityRequest = useMemo(() => {
    if (customerWallets?.length) {
      const walletLsids = customerWallets?.map(wallet => wallet?.id)

      return getWalletActionCapabilityRequestFilters(
        walletLsids,
        enableFiatPayinCapabilityV2,
        enableFiatPayoutCapabilityV2
      )
    }
  }, [customerWallets])

  const {
    data: bulkCapabilities,
    isFetching: isFetchingCapabilities,
    isError: isErrorFetchingCapabilities,
  } = useBulkCapabiltiesQuery(fiatWalletCapabilitiesRequest, !!fiatWalletCapabilitiesRequest)

  useEffect(() => {
    setCurrentCustomerWallet(null)
  }, [])

  const { customerDetails, isFetchingCountries } = useCustomerWalletDepositDetails(customer)

  useEffect(() => {
    setCurrentCustomerAtom(customer)
    return () => {
      setCurrentCustomerAtom(null)
    }
  }, [customer])

  const handleOnAddCustomerWallet = () => {
    setIsAddWalletSheetOpen(true)
  }

  const handleAddWalletSheetClose = () => {
    setIsAddWalletSheetOpen(false)
  }
  const {
    isSidePanelOpen: isCustomerWalletSidePanelOpen,
    sidePanelData: customerWalletData,
    paging: pagingCustomerWalletPaging,
    setIsSidePanelOpen: setIsCustomerWalletSidePanelOpen,
    updateSidePanel: updateCustomerWalletSidePanel,
  } = useSidePanel(customerWallets)

  const handleOnDetailsClick = (index: number) => {
    updateCustomerWalletSidePanel(index)
    setIsCustomerWalletSidePanelOpen(true)
  }

  const handleOpenFiatDepositSheet = (customerWallet: CustomerWallet) => {
    if (customerWallet) {
      const customerWalletDepositDetails: CustomerWalletDepositDetails = {
        wallet: customerWallet,
        customer: customer,
      }
      setWalletAction({
        actionId: ActionIds?.FIAT_DEPOSIT,
        header: t('deposit'),
        onClose: () => {
          setIsSheetOpen(false)
        },
        meta: {
          customerWalletDepositDetails,
        },
      })
    }
    setIsSheetOpen(true)
  }

  const handleOpenMoveSheet = (wallet: CustomerWallet) => {
    if (wallet) {
      setWalletAction({
        actionId: ActionIds.MOVE,
        header: t('customers.moveFunds'),
        onClose: () => {
          setIsSheetOpen(false)
        },
        meta: {
          sourceWalletType: WalletTransferType?.CUSTOMER,
          sourceCustomerWallet: wallet,
          customerModel,
        },
      })
    }
    setIsSheetOpen(true)
  }

  const handleExportTransactionClick = (customerWallet: CustomerWallet) => {
    setCurrentCustomerWallet(customerWallet)
    setIsTransactionExportModalVisible(true)
  }

  const handleOnExportModalClose = () => {
    setIsTransactionExportModalVisible(false)
    setCurrentCustomerWallet(null)
  }

  const cleanupUnifiedPayoutsFlow = () => {
    setBeneficiaryAtom(null)
    setFiatFormAtom(null)
    setFiatBeneficiaryAtom(null)
    setSourceWalletAtom(null)
    setWalletAction(null)
    setIsSheetOpen(false)
  }

  const handleOpenSendFiatSheet = (customerWallet: CustomerWallet) => {
    if (customerWallet) {
      setWalletAction({
        actionId: ActionIds?.FIAT_SEND,
        header: t('wallets.fiatSend.beneficiaryStepHeader'),
        onClose: cleanupUnifiedPayoutsFlow,
        sourceWalletReference: customerWallet?.id,
        meta: {
          customerWallet: customerWallet,
        },
      })

      setIsSheetOpen(true)
    }
  }

  const handleOpenSendCryptoSheet = (customerWallet: CustomerWallet) => {
    if (customerWallet) {
      setWalletAction({
        actionId: ActionIds?.SEND,
        header: t('wallets.send.contactStepHeader'),
        onClose: () => {
          setIsSheetOpen(false)
        },
        meta: {
          customerWallet: customerWallet,
        },
      })
      setIsSheetOpen(true)
    }
  }

  const handleWalletStatusUpdate = (wallet: CustomerWallet, action: WalletLifecycleAction) => {
    switch (action) {
      case WalletLifecycleActionTypes.BLOCK:
        setWalletDialogState({ wallet, dialogType: WalletDialogTypes.BLOCK })
        break
      case WalletLifecycleActionTypes.UNBLOCK:
        setWalletDialogState({ wallet, dialogType: WalletDialogTypes.UNBLOCK })
        break
      case WalletLifecycleActionTypes.TERMINATE: {
        const hasFunds = Number(wallet?.balance?.value) > 0
        setWalletDialogState({
          wallet,
          dialogType: hasFunds ? WalletDialogTypes.HAS_FUNDS : WalletDialogTypes.TERMINATE,
        })
        break
      }
    }
  }

  const closeWalletDialog = () => setWalletDialogState(null)

  const walletActionForDialog = DIALOG_TYPE_TO_ACTION_MAP[walletDialogState?.dialogType]

  const isActiveCustomer = customer?.status === CustomerStatus.VERIFIED
  const walletTableRows = useCustomerWalletsTableRowData({
    bulkCapabilities,
    isFetchingCapabilities,
    customerWallets,
    isActiveCustomer,
    handleExportTransactionClick,
    handleOnDetailsClick,
    handleOpenMoveSheet,
    handleOpenFiatDepositSheet,
    handleOpenSendFiatSheet,
    handleOpenSendCryptoSheet,
    handleWalletStatusUpdate,
  })

  const onReloadWalletsData = () => {
    isErrorFetchingCustomerWallets && fetchCustomerWallets()
  }

  const isAddWalletEnabled = useMemo(() => {
    if ((!canEdit && !canTrade) || !isActiveCustomer) return false

    if (isEmbeddedOrRelianceCustomerModel) {
      return isEpcEmbeddedCustomerWalletCreationCapabilityEnabled
    }

    return true
  }, [
    canEdit,
    isEmbeddedOrRelianceCustomerModel,
    isActiveCustomer,
    canTrade,
    isEpcEmbeddedCustomerWalletCreationCapabilityEnabled,
  ])

  const AddWalletButton = isAddWalletEnabled && (
    <Button
      iconElement={<Icon name="Plus" />}
      disabled={isViewOnlyRole}
      onClick={handleOnAddCustomerWallet}
    >
      {t('customers.addWallet')}
    </Button>
  )

  const AddWalletButtonWithoutPlusIcon = isAddWalletEnabled && (
    <Button disabled={isViewOnlyRole} onClick={handleOnAddCustomerWallet}>
      {t('customers.addWallet')}
    </Button>
  )

  const CustomerWalletsView = useMemo(() => {
    if (isErrorFetchingCustomerWallets || isErrorFetchingCapabilities) {
      return <RequestErrorStatusCard onReload={onReloadWalletsData} />
    }
  }, [isErrorFetchingCustomerWallets, isErrorFetchingCapabilities])

  const CustomerInfoView = useMemo(() => {
    if (isFetchingCustomerInfo || isFetchingCountries) {
      return <CustomerInfoSkeletonLoader />
    }
    if (customerDetails) {
      return <CustomerInfo customerDetails={customerDetails} />
    }
  }, [isFetchingCustomerInfo, isFetchingCountries, customerDetails])

  const customerTabs: Tab[] = [
    {
      id: CustomerDetailTabs.OVERVIEW,
      header: t('customers.tabs.overview'),
      content: null,
      testId: 'customer-overview-tab',
    },
    {
      id: CustomerDetailTabs.PAYMENT_LIMITS,
      header: t('customers.tabs.paymentLimits'),
      content: null,
      testId: 'customer-payment-limits-tab',
    },
  ]

  const iconUrl = customerWalletData?.balance?.currencyCode
    ? `${process.env.ICONS_URL}${customerWalletData.balance.currencyCode.toLowerCase()}.svg`
    : undefined

  return (
    <>
      <PageHeader
        description={
          isEmbeddedOrRelianceCustomerModel
            ? t('embeddedCustomers.details.description')
            : t('customers.customerDetailsDescription')
        }
        renderRightSection={
          activeTab === CustomerDetailTabs.OVERVIEW && hasAnyWallets && AddWalletButton
        }
      />
      {isErrorFetchingCustomerInfo ? (
        <RequestErrorStatusCard onReload={refetchCustomerInfo} />
      ) : (
        <>
          {enablePaymentLimitsManagement && (
            <Box paddingB={16}>
              <Tabs
                tabs={customerTabs}
                defaultTabId={activeTab}
                onChange={tabId => setActiveTab(tabId as CustomerDetailTab)}
                testId="customer-detail-tabs"
              />
            </Box>
          )}
          {activeTab === CustomerDetailTabs.OVERVIEW && (
            <CustomerLayout
              CustomerInfo={CustomerInfoView}
              CustomerWallets={
                <>
                  {CustomerWalletsView ?? (
                    <CustomerWallets
                      filterProps={filterProps}
                      isCustomerVerified={isActiveCustomer}
                      customerName={customerDetails?.name}
                      isLoading={isFetchingWalletsData || isFetchingCapabilities}
                      walletTableRows={walletTableRows}
                      AddWalletButton={AddWalletButtonWithoutPlusIcon}
                    />
                  )}
                  {pagination.isVisible && (
                    <Box
                      className={clsx({
                        'opacity-25': isFetchingCustomerWallets,
                      })}
                    >
                      <Pagination
                        testId="customer-wallets-pagination"
                        page={pagination.page}
                        rowsPerPage={pagination.rowsPerPage}
                        total={pagination.paging?.total ?? 0}
                        onChangePage={pagination.onChangePage}
                        onChangeRowsPerPage={pagination.onChangeRowsPerPage}
                      />
                    </Box>
                  )}
                </>
              }
            />
          )}
          {activeTab === CustomerDetailTabs.PAYMENT_LIMITS && (
            <CustomerLayout
              CustomerInfo={CustomerInfoView}
              CustomerWallets={<CustomerPaymentLimitsTable customerReference={customerReference} />}
            />
          )}
        </>
      )}

      <SidePanel
        id={'side-panel-customer-wallet'}
        isOpen={isCustomerWalletSidePanelOpen}
        onClose={() => setIsCustomerWalletSidePanelOpen(false)}
        paging={pagingCustomerWalletPaging}
        bodyScrollable={false}
        activeRowId={customerWalletData?.id}
        onActiveRowChange={highlightRowBasedOnId}
        topOffset={bannerSpacing}
        header={
          <SidePanelHeaderTemplateTwo
            heading={{
              iconUrls: [iconUrl],
              primaryLabel: customerWalletData?.name,
              secondaryLabel: `${roundNumberWithCommas(Number(customerWalletData?.balance?.value) || 0, 2)} ${customerWalletData?.balance?.currencyCode}`,
            }}
          />
        }
        body={
          <Container flex flexCol gapY={12} width="full">
            <CustomerWalletSidepanelDetails customerWalletData={customerWalletData} />
          </Container>
        }
      />

      <Sheet
        isOpen={isAddWalletSheetOpen}
        header={t('customers.newWallet')}
        onClose={handleAddWalletSheetClose}
      >
        <AddWallet walletType={WalletTypes?.CUSTOMER} handleClose={handleAddWalletSheetClose} />
      </Sheet>

      <WalletSheet isSheetOpen={isSheetOpen} />

      {isTransactionExportModalVisible && (
        <WalletTransactionExportModal
          key={currentCustomerWallet?.id}
          handleClose={handleOnExportModalClose}
          downloadAll={false}
          customerWallet={currentCustomerWallet}
        />
      )}

      {walletActionForDialog && walletDialogState && (
        <WalletActionsDialog
          walletId={walletDialogState.wallet.id}
          walletName={walletDialogState.wallet.name}
          action={walletActionForDialog}
          onClose={closeWalletDialog}
          invalidateQueryKeys={[[reactQueryName.CUSTOMER_WALLETS]]}
        />
      )}

      {walletDialogState?.dialogType === WalletDialogTypes.HAS_FUNDS && (
        <WalletHasFundsDialog
          walletName={walletDialogState.wallet.name}
          onClose={closeWalletDialog}
          onMoveFunds={() => {
            handleOpenMoveSheet(walletDialogState.wallet)
            closeWalletDialog()
          }}
        />
      )}
    </>
  )
}
